/* global Hurdlr */
import { useEffect, useState } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/styles';

import AnimatedLoading from '#client/components/AnimatedLoading';
import manifest from '#client/manifest';

import generalEventListeners from './EventListeners';
import MerchantOnboardingMessage from './MerchantOnboardingMessage';

import VideoCard from '../../components/VideoCard/VideoCard';
import MobileMessage from '../Money/components/MobileMessage';
import { SCREEN_BY_ROUTE } from '../Money/utils/constants';

export const HURDLR_SESSION_DATA = gql`
  query HurdlrSessionData {
    hurdlrSession {
      accessToken
      clientId
      environment
      mixpanelProjectToken
    }
  }
`;

const HurdlrView = styled('div')(({ theme, isOverviewPage }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: '0',
  left: '0',
  [theme.breakpoints.up('lg')]: {
    left: isOverviewPage ? '0' : '1.25rem',
    height: isOverviewPage ? '100%' : 'calc(100% + 20px)'
  }
}));

const VideoContainer = styled('div')(({ theme }) => ({
  marginBottom: '0.5rem',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: '5.25rem',
    paddingRight: '3.25rem'
  }
}));

const ExpenseTrackerWebSdkUI = ({
  className,
  id,
  invoiceCallback,
  pathname,
  plaidListenerCallback,
  style,
  tileListenerCallback
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const viewName = SCREEN_BY_ROUTE[pathname.split('/').pop()] || 'home';
  const { fintech: { hurdlrWebSdkStableScriptSrc } } = manifest;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPC = useMediaQuery(theme.breakpoints.up('lg'));
  const showInvoicesVideo = useExperimentFlag('enable-zbm-invoices-video', false);
  const showMoneyProOnMobile = useExperimentFlag('enable-money-pro-on-mobile', false);
  const shouldBeMobileViewport = !isPC;

  const {
    data,
    loading
  } = useQuery(HURDLR_SESSION_DATA);

  const hurdlrInitData = data?.hurdlrSession;

  useEffect(() => {
    const existingScript = document.getElementById('hurdlrSdkScript');

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = hurdlrWebSdkStableScriptSrc;
      script.async = true;
      script.id = 'hurdlrSdkScript';
      document.body.appendChild(script);
      script.onload = () => setIsScriptLoaded(true);
    }

    if (existingScript && !isScriptLoaded) {
      setIsScriptLoaded(true);
    }
  }, [hurdlrWebSdkStableScriptSrc, isScriptLoaded]);

  useEffect(() => {
    if (hurdlrInitData && isScriptLoaded && !loading) {
      if (typeof Hurdlr !== 'undefined') {
        if (!Hurdlr.initialized) {
          Hurdlr.init({
            ...hurdlrInitData,
            // "viewport" is ignored in screens that are not "Invoice"
            // We are not adding a check for whether we're in the
            // Invoice screen because they'll likely
            // use this key for the other screens in the future.
            viewport: shouldBeMobileViewport ? 'mobile' : undefined
          });
          Hurdlr.registerLearnMoreListener(generalEventListeners);
          // only shows up in the income page
          Hurdlr.registerIncomeDashCTAListener(generalEventListeners);
        }
        if (tileListenerCallback && viewName === SCREEN_BY_ROUTE.tiles) {
          Hurdlr.registerTileListener(tileListenerCallback);
        }
        if (plaidListenerCallback) {
          Hurdlr.registerPlaidLinkListener(plaidListenerCallback);
        }
        if (invoiceCallback
            && (viewName === SCREEN_BY_ROUTE.tiles
            || viewName === SCREEN_BY_ROUTE.invoices)) {
          Hurdlr.registerInvoiceListener(invoiceCallback);
        }
        Hurdlr.renderScreen(`hurdlr-container${id ? `-${id}` : ''}`, viewName);
      }
    }
  }, [
    hurdlrInitData,
    id,
    isScriptLoaded,
    loading,
    plaidListenerCallback,
    tileListenerCallback,
    invoiceCallback,
    viewName,
    shouldBeMobileViewport
  ]);

  if (loading || !isScriptLoaded) {
    return <AnimatedLoading width={232} />;
  }

  const isOverviewPage = viewName === SCREEN_BY_ROUTE.tiles;
  const isExpensePage = viewName === SCREEN_BY_ROUTE.expenses;
  const isInvoicePage = viewName === SCREEN_BY_ROUTE.invoices;
  const showHurdlrComponent = !isMobile || showMoneyProOnMobile || isInvoicePage;

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {isInvoicePage && <MerchantOnboardingMessage />}
      {isExpensePage && (
        <VideoContainer>
          <VideoCard
            pageTitle="Expenses"
            video="expenses"
          />
        </VideoContainer>
      )}
      {isInvoicePage && showInvoicesVideo && (
        <VideoContainer>
          <VideoCard
            video="invoices"
          />
        </VideoContainer>
      )}
      {showHurdlrComponent ? (
        <Box position="relative" flex="1">
          <HurdlrView
            className={className}
            id={`hurdlr-container${id ? `-${id}` : ''}`}
            style={style}
            data-testid="hurdlr-container"
            isOverviewPage={isOverviewPage}
          />
        </Box>
      ) : (
        <Box marginTop="1.5rem">
          <MobileMessage page={viewName} />
        </Box>
      )}
    </Box>
  );
};

ExpenseTrackerWebSdkUI.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  invoiceCallback: PropTypes.func,
  pathname: PropTypes.string.isRequired,
  plaidListenerCallback: PropTypes.func,
  style: PropTypes.shape({}),
  tileListenerCallback: PropTypes.func
};

ExpenseTrackerWebSdkUI.defaultProps = {
  className: '',
  id: '',
  invoiceCallback: null,
  plaidListenerCallback: null,
  style: {},
  tileListenerCallback: null
};

export default ExpenseTrackerWebSdkUI;
